import type { ReactNode } from 'react';
import { PageHeader } from './PageHeader';
import { TopBarAction } from './topBarAction';

interface Props {
  eyebrow?: string;
  title: string;
  titleAccent?: string;
  subtitle?: string;
  /**
   * Inline slot rendered on the right side of the page header row, level
   * with the subtitle baseline. Use for status tab pills / segmented
   * controls. Forwarded to `PageHeader.rightSlot`.
   */
  headerRightSlot?: ReactNode;
  /** Buttons to render in the AppShell topbar's action slot. */
  topBarAction?: ReactNode;
  /**
   * When true, the page body is rendered full-height with no internal scroll.
   * Use for screens that own their own scroll (e.g. ag-grid). Default is a
   * normal scroll-on-overflow document flow.
   */
  fullHeight?: boolean;
  children: ReactNode;
}

export function Page({
  eyebrow,
  title,
  titleAccent,
  subtitle,
  headerRightSlot,
  topBarAction,
  fullHeight = false,
  children,
}: Props) {
  return (
    <div
      className={[
        'relative animate-fade-in',
        fullHeight ? 'flex h-full flex-col' : '',
      ].join(' ')}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute right-0 top-0 h-72 w-72 rounded-full blur-3xl"
        style={{ background: 'rgb(var(--color-primary) / 0.05)' }}
      />

      <div
        className={[
          'relative mx-auto w-full max-w-[1400px] px-4 pt-4 md:px-8 md:pt-6',
          fullHeight ? 'flex flex-1 flex-col pb-4' : 'pb-6 md:pb-8',
        ].join(' ')}
      >
        <PageHeader
          eyebrow={eyebrow}
          title={title}
          titleAccent={titleAccent}
          subtitle={subtitle}
          rightSlot={headerRightSlot}
        />
        {fullHeight ? (
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        ) : (
          children
        )}
      </div>

      {topBarAction && <TopBarAction>{topBarAction}</TopBarAction>}
    </div>
  );
}
